import type { DropDownItem } from "../../pojo/DropDownItem";
import type { Nullable } from "../../pojo/Util";
import type { ReservationAttemptDetails } from "../../modules/s25-chat-bot/s25.express.scheduling.chat.bot";
import type { S25ItemI } from "../../pojo/S25ItemI";

/** Rose event state used when saving express events. */
export const EXPRESS_SAVE_EVENT_STATE = 2;

/** Single-date profile code for express event form model seeding. */
export const EXPRESS_PROFILE_CODE = "dnr";

export const EXPRESS_MIN_CREATE_YEARS_PAST = 5;
export const EXPRESS_MAX_CREATE_YEARS_FUTURE = 10;

/** Legacy settle delay after seeding the date model / location dropdown. */
export const EXPRESS_FORM_SETUP_DELAY_MS = 250;

/** Microtask yield between location, quota, and availability promise batches. */
export const EXPRESS_PROMISE_YIELD_MS = 1;

/**
 * Rose `parent_id` when no folder has been chosen yet.
 * {@link EventService.postExpressEvent} requires a number; express passes this so Rose can auto-route
 * (or respond with `EV_I_MULTPAR` for cabinet-select).
 */
export const EXPRESS_EVENT_PARENT_UNASSIGNED = 0;

/** Folder/cabinet id chosen in cabinet-select after an `EV_I_MULTPAR` response. */
export type ExpressSchedulingSelectedParentId = number;

/** Options for {@link S25ExpressSchedulingHost.createExpressEvent}. */
export type ExpressSchedulingSaveOptions = {
    /** Set only when retrying save after the user picks a parent folder. */
    selectedParentId?: ExpressSchedulingSelectedParentId;
    /** Skip past-date validation (AI save already validated the range). */
    datesAlreadyChecked?: boolean;
};

/** Maps express scheduling parent state to the value {@link EventService.postExpressEvent} expects. */
export function toRoseExpressParentId(selectedParentId?: ExpressSchedulingSelectedParentId): number {
    return selectedParentId ?? EXPRESS_EVENT_PARENT_UNASSIGNED;
}

/** Modal or deep-link seed data passed when opening express scheduling. */
export type ExpressSchedulingInitContext = {
    itemId?: number;
    startDt?: Date;
    endDt?: Date;
    closeModal?: () => void;
};

/** Dashboard panel host API: permission flags and draft model accessors. */
export type ExpressSchedulingPanelApi = {
    hasExpressPerms?: boolean;
    hasNoExpressPerms?: boolean;
    init?: boolean;
    getModel?: () => {
        name: string;
        startDt: Date;
        endDt: Date;
        location: Nullable<ExpressSchedulingLocationItem>;
    };
    reset?: () => void;
};

export type ExpressSchedulingLocationItem = DropDownItem<number> & {
    duration?: number;
    direct_schedule?: { direct_schedule_duration?: string };
    eventTypeId?: number;
    scheduler?: { itemId?: number };
    checkedObj?: { hasConflicts?: boolean };
};

/** Populated by {@link ExpressSchedulingDateModel.getQuotaMsg} after quota checks. */
export type ExpressSchedulingQuotaConflict = {
    location?: ExpressSchedulingLocationItem | null;
    [field: string]: unknown;
};

export type ExpressSchedulingTimeRange = {
    startDt: Date;
    endDt: Date;
};

export type ExpressSchedulingSaveLocation = ExpressSchedulingLocationItem & {
    itemId: number;
    eventTypeId: number;
    /** When absent, {@link EventService.postExpressEvent} falls back to the current contact. */
    scheduler?: { itemId?: number };
};

/** `s25-location-dropdown` / generic dropdown two-way API surface. */
export type ExpressSchedulingLocationDropdownApi = {
    getItems?: () => ExpressSchedulingLocationItem[];
} & Record<string, unknown>;

/** HTTP error shape from legacy AngularJS `$http` / DataAccess failures. */
export type ExpressSchedulingHttpError = {
    data?: unknown;
};

/** Message entries on express save response payloads. */
export type ExpressEventSaveMessage = {
    msg_text?: string;
};

/** Subset of user-group Rose security prefs used after express save. */
export type ExpressUserGroupRoseSecurity = {
    creation_success_msg?: string;
};

/** Event form model after `EventFormModelUtil.decorateModelMethods` (AngularJS). */
export type ExpressSchedulingDateModel = {
    timeModel: {
        /** Null only before default range is seeded from {@link DatetimeService}. */
        evStartDt: Date | null;
        evEndDt: Date | null;
        minutes: Record<string, unknown>;
    };
    locations: ExpressSchedulingLocationItem[];
    occurrences: Array<{
        evStartDt?: Date;
        evEndDt?: Date;
        hasQuotaConflict?: boolean;
        quotaConflict?: ExpressSchedulingQuotaConflict;
    }>;
    minCreateDate?: Date;
    maxCreateDate?: Date;
    onInitDateTimeChange?: () => unknown;
    removeObject: (item: ExpressSchedulingLocationItem) => void;
    addObject: (item: ExpressSchedulingLocationItem, flag: boolean) => Promise<unknown>;
    setObjectDetails: (items: ExpressSchedulingLocationItem[]) => Promise<unknown>;
    checkAvailability: (
        model: ExpressSchedulingDateModel,
        locations: ExpressSchedulingLocationItem[],
    ) => Promise<unknown>;
    checkQuotas: (
        model: ExpressSchedulingDateModel,
        location: ExpressSchedulingLocationItem | ExpressSchedulingLocationItem[],
    ) => Promise<unknown>;
    getQuotaMsg: (conflict: ExpressSchedulingQuotaConflict) => string;
    itemDict?: {
        type?: { data?: S25ItemI };
        [field: string]: unknown;
    };
    addOccurrence: (
        startDt: Date,
        endDt: Date,
        extra: Record<string, unknown>,
        flag: boolean,
        profileCode: string,
    ) => unknown;
};

export type EventFormModelUtilFactory = {
    decorateModelMethods: (model: Record<string, unknown>) => ExpressSchedulingDateModel;
};

/** Passed to `s25-location-dropdown` `[express]` — filters locations by availability for the current range. */
export type ExpressSchedulingLocationFilter = {
    startDt: Date;
    endDt: Date;
    getAvailableItems: (items: ExpressSchedulingLocationItem[]) => Promise<ExpressSchedulingLocationItem[]>;
};

export type ExpressSchedulingAiSaveHandler = (reservationAttemptDetails: ReservationAttemptDetails) => Promise<unknown>;

/** Error payload when Rose cannot auto-route an express event (`msg_id: EV_I_MULTPAR`). */
export type ExpressSchedulingMultiParentError = {
    msg_id?: "EV_I_MULTPAR";
    parent?: unknown;
};
